'use client';

import { Suspense } from 'react';
import { useTranslations } from 'next-intl';
import { TransformedLeadershipData, LeadershipComponent } from '@/types/leadership';
import CommonSpacer from '@/components/sections/shared/CommonSpacer';
import CoreTeamHero from '../components/CoreTeamHero';
import CoreTeamPromoters from '../components/CoreTeamPromoters';
import CoreTeamFeatured from '../components/CoreTeamFeatured';
import CoreTeamChairmanMessage from '../components/CoreTeamChairmanMessage';
import CoreTeamSkeleton from '../components/CoreTeamSkeleton';

interface CoreTeamClientProps {
  locale: string;
  leadershipData: TransformedLeadershipData | null;
}

function renderLeadershipComponent(component: LeadershipComponent) {
  switch (component.__component) {
    case 'common.spacer-top':
    case 'common.spacer-full':
    case 'common.spacer-bottom':
      return <CommonSpacer key={`${component.__component}-${component.id}`} zone={component} />;

    case 'leadership.promoter-section':
      return (
        <Suspense key={`promoters-${component.id}`} fallback={<CoreTeamSkeleton.Stats />}>
          <CoreTeamPromoters section={component} />
        </Suspense>
      );

    case 'leadership.management-section':
      return (
        <Suspense key={`management-${component.id}`} fallback={<CoreTeamSkeleton.Featured />}>
          <CoreTeamFeatured section={component} />
        </Suspense>
      );

    case 'modules.simple-text-image':
      return (
        <Suspense key={`chairman-${component.id}`} fallback={<CoreTeamSkeleton.Message />}>
          <CoreTeamChairmanMessage section={component} />
        </Suspense>
      );

    default:
      return null;
  }
}

export default function CoreTeamClient({ locale, leadershipData }: CoreTeamClientProps) {
  const t = useTranslations('coreTeam');

  if (!leadershipData) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('hero.title')}</h1>
          <p className="text-xl text-gray-600">{t('error.loadFailed')}</p>
        </div>
      </main>
    );
  }

  const components = leadershipData.components ?? [];

  return (
    <main className="min-h-screen">
      <Suspense fallback={<CoreTeamSkeleton.Hero />}>
        <CoreTeamHero hero={leadershipData.hero} />
      </Suspense>

      {components.length > 0
        ? components.map((component) => renderLeadershipComponent(component))
        : null}
    </main>
  );
}
